import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Landing from "@/pages/site/Landing";
import RequestAccess from "@/pages/site/RequestAccess";
import Activate from "@/pages/Activate";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminApplications from "@/pages/admin/AdminApplications";
import { AdminCustomers, AdminCustomerDetail, AdminAudit } from "@/pages/admin/AdminCustomers";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SupplierPortal from "./pages/SupplierPortal";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Scan from "@/pages/Scan";
import Invoices from "@/pages/Invoices";
import InvoiceReview from "@/pages/InvoiceReview";
import Suppliers from "@/pages/Suppliers";
import SupplierDetail from "@/pages/SupplierDetail";
import Products from "@/pages/Products";
import PackagingReview from "@/pages/PackagingReview";
import ProductDetail from "@/pages/ProductDetail";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";
import Order from "@/pages/Order";
import OrderReview from "@/pages/OrderReview";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Admin from "@/pages/Admin";
import PriceComparison from "@/pages/PriceComparison";
import StatementDetail from "@/pages/StatementDetail";
import { OrderProvider } from "@/context/OrderContext";

function Loading() {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-950 text-slate-300" data-testid="app-loading">
      <div className="flex items-center gap-3"><span className="h-3 w-3 rounded-full bg-emerald-400 scan-pulse" /> Loading Supplivo…</div>
    </div>
  );
}

function Protected({ children, ownerOnly = false, superadminOnly = false, perm = null }) {
  const { session, isOwner, user, can } = useAuth();
  if (session === null) return <Loading />;
  if (session === false) return <Navigate to="/login" replace />;
  if (ownerOnly && !isOwner) return <Navigate to="/scan" replace />;
  if (perm && !can(perm)) return <Navigate to="/invoices" replace />;
  if (superadminOnly && !user?.is_superadmin) return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
}

function Public({ children }) {
  const { session } = useAuth();
  if (session === null) return <Loading />;
  if (session) return <Navigate to="/" replace />;  // Home picks the landing page by role
  return children;
}

function Home() {
  const { session, isOwner, user } = useAuth();
  if (session === null) return null;
  if (session === false) return <Landing />;  // public marketing site for visitors
  return <Navigate to={user?.is_superadmin ? "/admin" : isOwner ? "/dashboard" : "/scan"} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/request-access" element={<RequestAccess />} />
      <Route path="/get-started" element={<Navigate to="/request-access" replace />} />
      <Route path="/activate" element={<Activate />} />
      <Route path="/login" element={<Public><Login /></Public>} />
      <Route path="/register" element={<Navigate to="/request-access" replace />} />
      <Route path="/forgot-password" element={<Public><ForgotPassword /></Public>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/supplier/:token" element={<SupplierPortal />} />
      <Route path="/dashboard" element={<Protected ownerOnly><Dashboard /></Protected>} />
      <Route path="/scan" element={<Protected><Scan /></Protected>} />
      <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
      <Route path="/invoices/:id" element={<Protected><InvoiceReview /></Protected>} />
      <Route path="/suppliers" element={<Protected ownerOnly perm="manage_suppliers"><Suppliers /></Protected>} />
      <Route path="/suppliers/:id" element={<Protected ownerOnly perm="manage_suppliers"><SupplierDetail /></Protected>} />
      <Route path="/products" element={<Protected ownerOnly perm="manage_suppliers"><Products /></Protected>} />
      <Route path="/packaging-review" element={<Protected ownerOnly perm="manage_suppliers"><PackagingReview /></Protected>} />
      <Route path="/products/:id" element={<Protected ownerOnly perm="manage_suppliers"><ProductDetail /></Protected>} />
      <Route path="/prices" element={<Protected ownerOnly perm="view_prices"><PriceComparison /></Protected>} />
      <Route path="/statements" element={<Navigate to="/suppliers" replace />} />
      <Route path="/statements/:id" element={<Protected ownerOnly perm="review_invoices"><StatementDetail /></Protected>} />
      <Route path="/team" element={<Protected ownerOnly perm="manage_team"><Team /></Protected>} />
      <Route path="/order" element={<Protected><Order /></Protected>} />
      <Route path="/order/quick" element={<Protected><Order /></Protected>} />
      <Route path="/order/review" element={<Protected><OrderReview /></Protected>} />
      <Route path="/orders" element={<Protected><Orders /></Protected>} />
      <Route path="/orders/:id" element={<Protected><OrderDetail /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/admin" element={<Protected superadminOnly><AdminOverview /></Protected>} />
      <Route path="/admin/applications" element={<Protected superadminOnly><AdminApplications /></Protected>} />
      <Route path="/admin/customers" element={<Protected superadminOnly><AdminCustomers /></Protected>} />
      <Route path="/admin/customers/:id" element={<Protected superadminOnly><AdminCustomerDetail /></Protected>} />
      <Route path="/admin/audit" element={<Protected superadminOnly><AdminAudit /></Protected>} />
      <Route path="/admin/ai-usage" element={<Protected superadminOnly><Admin /></Protected>} />
      <Route path="*" element={<Protected><Home /></Protected>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <OrderProvider>
            <AppRouter />
          </OrderProvider>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
